/**
 * Internal/test traffic tagging.
 *
 * Email-based PostHog test-account filters can only exclude *identified* people.
 * Anonymous testing (browsing or signing up while logged out) carries no email,
 * so it leaks into analytics. To exclude it we stamp every event from a tester's
 * browser with a persistent `is_internal` super property and add a matching
 * `is_internal` event-property rule to the project's test-account filters.
 *
 * A tester turns the flag on by opening any page with `?internal=1` (and off
 * with `?internal=0`). The choice is persisted in localStorage so it survives
 * reloads and fresh anonymous sessions started from that bookmarked URL.
 */

const STORAGE_KEY = "bp_internal";

/** Super property attached to every event from an internal/test session. */
export const INTERNAL_SUPER_PROPERTY = "is_internal";

/**
 * Reconcile the persisted internal flag with the `internal` URL param and
 * return whether this browser is currently flagged as internal traffic.
 *
 * `?internal=1` / `?internal=true` enables; `?internal=0` / `?internal=false`
 * disables; anything else leaves the persisted value untouched.
 */
export function syncInternalFlagFromUrl(search: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = new URLSearchParams(search).get("internal");
    if (raw === "1" || raw === "true") {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } else if (raw === "0" || raw === "false") {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/** Whether this browser has been flagged as internal/test traffic. */
export function isInternalTraffic(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}
